import React, { Suspense, useCallback, useState } from 'react';
import { forTemplate } from '@trello/i18n';
import { useRecentlyUsedFeaturePreloader } from '@trello/recently-used-feature-preloader';
import { useLazyComponent } from '@trello/use-lazy-component';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { WorkspaceSwitcherTestIds } from '@trello/test-ids';
import { AllBoardsAndWorkspacesDocument } from './AllBoardsAndWorkspacesQuery.generated';
import { memberId } from '@trello/session-cookie';
import { HeaderMenu } from 'app/src/components/HeaderMenu';

const format = forTemplate('workspace_switcher');

const ErrorComponent: React.FC = () => <p>{format('error-loading-teams')}</p>;

export const WorkspaceSwitcher: React.FunctionComponent = () => {
  const [
    WorkspaceSwitcherPopover,
    trackFeatureUsage,
  ] = useRecentlyUsedFeaturePreloader({
    featureName: 'WorkspaceSwitcherPopover',
    useLazyComponentArgs: [
      () =>
        import(
          /* webpackChunkName: "workspace-switcher-popover" */ './WorkspaceSwitcherPopover'
        ),
      { namedImport: 'WorkspaceSwitcherPopover' },
    ],
    preloadQueryOptions: {
      query: AllBoardsAndWorkspacesDocument,
      variables: {
        memberId: memberId ?? 'me',
      },
    },
  });

  const [shouldHidePopover, setShouldHidePopover] = useState(false);

  const CreateWorkspaceOverlay = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-team-overlay" */ 'app/src/components/CreateTeamOverlay'
      ),

    {
      preload: false,
      namedImport: 'CreateTeamOverlay',
    },
  );

  const [showCreateWorkspaceOverlay, setShowCreateWorkspaceOverlay] = useState(
    false,
  );

  const closeOverlay = useCallback(() => {
    setShowCreateWorkspaceOverlay(false);
  }, [setShowCreateWorkspaceOverlay]);

  const onClickCreateTeam = useCallback(() => {
    setShowCreateWorkspaceOverlay(true);
    setShouldHidePopover(true);
  }, [setShowCreateWorkspaceOverlay, setShouldHidePopover]);

  const onWorkspaceClick = useCallback(() => {
    setShouldHidePopover(true);
  }, [setShouldHidePopover]);

  const resetShouldHidePopover = useCallback(() => {
    setShouldHidePopover(false);
  }, [setShouldHidePopover]);

  return (
    <>
      <HeaderMenu
        buttonText={format('teams')}
        analyticsButtonName="workspaceSwitcherButton"
        analyticsComponentName="workspaceSwitcherMenuInlineDialog"
        popoverTitle={format('teams')}
        dataTestId={WorkspaceSwitcherTestIds.WorkspaceSwitcher}
        shouldHidePopover={shouldHidePopover}
        resetShouldHidePopover={resetShouldHidePopover}
        noHorizontalPadding={true}
        onClick={trackFeatureUsage}
      >
        <ErrorBoundary
          tags={{
            ownershipArea: 'trello-teamplates',
            feature: 'Workspace Switcher',
          }}
          errorHandlerComponent={ErrorComponent}
        >
          <ChunkLoadErrorBoundary fallback={<ErrorComponent />}>
            <Suspense fallback={null}>
              <WorkspaceSwitcherPopover
                onCreateTeamOverlayOpen={onClickCreateTeam}
                onWorkspaceClick={onWorkspaceClick}
              />
            </Suspense>
          </ChunkLoadErrorBoundary>
        </ErrorBoundary>
      </HeaderMenu>
      {showCreateWorkspaceOverlay && (
        <ChunkLoadErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <CreateWorkspaceOverlay teamType="default" onClose={closeOverlay} />
          </Suspense>
        </ChunkLoadErrorBoundary>
      )}
    </>
  );
};
